import type { ActionCompletion } from '../types';

/**
 * The four states a suggested action's trigger (row / card / pill) can
 * render, derived from the SERVER completions map — not the client-only
 * actionStore. "executing" is a fifth state but lives entirely inside
 * ActionModal's own `mutation.isPending`, so it isn't part of this
 * derivation (see ActionModal.tsx).
 *
 * "suggested" is the default: no completion record exists yet, or the
 * caller couldn't correlate one (e.g. actionId missing on stale cached
 * data — see WhatsOnDeck's legacy fallback).
 */
export type ActionTriggerState = 'suggested' | 'done-this-batch' | 'needs-your-action' | 'failed';

/**
 * "pending" is the transient claim state written just before dispatch —
 * only ever observed if a page happens to load mid-flight, or the server
 * process crashed between claiming and finalizing. It renders the same as
 * "suggested" (clickable) rather than a fourth ambiguous badge: retrying
 * is always safe (the backend only blocks re-claiming "done" and
 * "needs-your-action", never "pending" or "failed" — see
 * _BLOCKING_COMPLETION_STATES in backend/app/routers/actions.py).
 */
export function deriveActionTriggerState(completion: ActionCompletion | undefined): ActionTriggerState {
  if (!completion) return 'suggested';
  switch (completion.state) {
    case 'done':
      return 'done-this-batch';
    case 'needs-your-action':
      return 'needs-your-action';
    case 'failed':
      return 'failed';
    case 'pending':
    default:
      return 'suggested';
  }
}

/** Only "done" and "needs-your-action" are terminal/non-retryable server-side
 * (see _BLOCKING_COMPLETION_STATES) — a click on either must reopen a
 * read-only summary rather than the editable form. "failed" (and the
 * transient "pending") remain retryable. */
export function isBlockingCompletion(completion: ActionCompletion | undefined): boolean {
  return completion?.state === 'done' || completion?.state === 'needs-your-action';
}

export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** "by {userName} · {relative time}" — Chris's literal ask, factored once
 * since it renders in three trigger components plus the modal's read-only
 * summary view. */
export function completionByLine(completion: ActionCompletion): string {
  const when = formatRelativeTime(completion.completedAt);
  return when ? `by ${completion.userName} · ${when}` : `by ${completion.userName}`;
}
